import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCompactDisc,
    faShuffle,
    faBackwardStep,
    faPlay,
    faPause,
    faForwardStep,
    faRepeat,
    faVolumeHigh,
    faMusic,
    faListUl,
    faCompress,
} from '@fortawesome/free-solid-svg-icons';
import './MusicPlayer.css';

// Link các bài hát mẫu kèm tiêu đề sẵn (tránh gọi 13 API noembed chặn mạng khi tải trang)
const musicTracks = [
    {
        src: "https://www.youtube.com/watch?v=Ye0L10K9jYg",
        name: "Nhạc Chill TikTok - Những Bản Nhạc Lofi Chill Nhẹ Nhàng"
    },
    {
        src: "https://www.youtube.com/watch?v=ClLPtPkygLg",
        name: "Nếu Ngày Ấy, Xin Đừng Lặng Im, Chiều Hôm Ấy - Top Hits Nhạc Ballad"
    },
    {
        src: "https://www.youtube.com/watch?v=wqOEGEfGAXQ",
        name: "Yêu một người bằng hết con tim là đúng hay sai? - Ballad Chill"
    },
    {
        src: "https://www.youtube.com/watch?v=FJKfOaTZOEY",
        name: "Nhạc Buồn Suy - Những Bản Lofi Tâm Trạng Hay Nhất"
    },
    {
        src: "https://www.youtube.com/watch?v=asX9NY35BfQ",
        name: "Tìm Em - Anh Hãy Thôi Chờ Em Đi Anh Nhé | Nhạc Lofi Buồn"
    },
    {
        src: "https://www.youtube.com/watch?v=mnQMvKnvEKs",
        name: "Ngày Em Đẹp Nhất - Tama x Bell「Lofi Ver」"
    },
    {
        src: "https://www.youtube.com/watch?v=UAE8LQLF5-8",
        name: "Bình Yên, Những Lời Hứa Bỏ Quên, Lạ Lùng - Hits Của VŨ"
    },
    {
        src: "https://www.youtube.com/watch?v=ZVPVzM61Hhw",
        name: "TOP Bản Nhạc Lofi Chill - Nhạc Ballad Chill Nhẹ Nhàng"
    },
    {
        src: "https://www.youtube.com/watch?v=GkLYI9Uv240",
        name: "Nhạc Lofi Thư Giãn Học Bài & Làm Việc"
    },
    {
        src: "https://www.youtube.com/watch?v=BB8cNVJVtaA",
        name: "Nhạc Ballad Nhẹ Nhàng - Nhạc 8x 9x Đời Đầu Hay Nhất"
    },
    {
        src: "https://www.youtube.com/watch?v=5Z9RMvNc5Os",
        name: "Playlist Tổng Hợp Top Hit Ca Sĩ Giấu Mặt | Tự Dưng Thành Người Lạ"
    },
    {
        src: "https://www.youtube.com/watch?v=m0HO9MzJOYE",
        name: "Nhạc Chill TikTok - Những Bản Hits Nhạc Trẻ Triệu View"
    },
    {
        src: "https://www.youtube.com/watch?v=zQ7GPAQVXDk",
        name: "Tình Xưa Nghĩa Cũ (Lofi ver) Đạt Long Vinh"
    },
];

// Helper: Trích xuất Youtube ID từ link
const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

// Helper: Shuffle an array using Fisher-Yates algorithm
const shuffleArray = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
};

// --- Sub-components ---

const TrackInfo = ({ title, currentTime, duration, isPlaying }) => {
    const formatTime = (time) => {
        if (!time || isNaN(time)) return "0:00";
        const min = Math.floor(time / 60);
        const sec = Math.floor(time % 60);
        return `${min}:${sec < 10 ? '0' + sec : sec}`;
    };

    const titleRef = useRef(null);
    const [shouldMarquee, setShouldMarquee] = useState(false);

    useEffect(() => {
        const checkMarquee = () => {
            if (titleRef.current && titleRef.current.parentElement) {
                const isOverflow = titleRef.current.scrollWidth > titleRef.current.parentElement.clientWidth;
                setShouldMarquee(isOverflow);
            }
        };

        const timer = setTimeout(checkMarquee, 100);
        return () => clearTimeout(timer);
    }, [title]);

    return (
        <div className="mp-info-container">
            <div className={`mp-track-icon ${isPlaying ? 'spinning' : ''}`}>
                <FontAwesomeIcon icon={faCompactDisc} />
            </div>
            <div className="mp-info-text" style={{ width: '150px' }}>
                <div className="mp-title-wrapper">
                    <div
                        ref={titleRef}
                        className={`mp-title ${shouldMarquee ? 'marquee' : ''} ${!isPlaying ? 'paused' : ''}`}
                    >
                        <span className="mp-title-text">{title || "Đang tải tên bài hát..."}</span>
                        {shouldMarquee && <span className="mp-title-text">{title}</span>}
                    </div>
                </div>
                <div className="mp-status">
                    <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                </div>
            </div>
        </div>
    );
};

const ProgressBar = ({ currentTime, duration, onSeek }) => {
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    return (
        <div className="mp-progress-container">
            <div className="mp-progress-bg">
                <div className="mp-progress-bar" style={{ width: `${progress}%` }}></div>
            </div>
            <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                step="0.5"
                className="mp-seek-slider"
                onChange={onSeek}
            />
        </div>
    );
};

const Controls = ({ isPlaying, isShuffle, loopMode, volume, onTogglePlay, onNext, onPrev, onShuffle, onLoop, onVolumeChange }) => {
    return (
        <div className="mp-controls" style={{ justifyContent: 'center', marginTop: '8px' }}>
            <button className={`mp-btn ${isShuffle ? 'active' : ''}`} onClick={onShuffle} title="Shuffle">
                <FontAwesomeIcon icon={faShuffle} style={{ fontSize: '12px' }} />
            </button>
            <button className="mp-btn" onClick={onPrev} title="Previous">
                <FontAwesomeIcon icon={faBackwardStep} />
            </button>
            <button className="mp-btn main-c" onClick={onTogglePlay} title={isPlaying ? "Pause" : "Play"}>
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} style={!isPlaying ? { paddingLeft: '2px' } : {}} />
            </button>
            <button className="mp-btn" onClick={onNext} title="Next">
                <FontAwesomeIcon icon={faForwardStep} />
            </button>
            <button className={`mp-btn ${loopMode !== 'none' ? 'active' : ''}`} onClick={onLoop} title="Loop">
                <FontAwesomeIcon icon={faRepeat} style={{ opacity: loopMode === 'none' ? 0.5 : 1 }} />
                {loopMode === 'one' && <span style={{ position: 'absolute', fontSize: '8px', top: '2px', right: '4px' }}>1</span>}
            </button>
            <div className="vol-container">
                <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={volume * 100}
                    className="vol-slider"
                    onChange={(e) => onVolumeChange(parseFloat(e.target.value) / 100)}
                />
            </div>
        </div>
    );
};

const Playlist = ({ tracks, currentIndex, onSelect }) => {
    return (
        <motion.div
            className="mp-playlist"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
        >
            {tracks.map((track, idx) => (
                <div
                    key={idx}
                    className={`mp-track-item ${currentIndex === idx ? 'active' : ''}`}
                    onClick={() => onSelect(idx)}
                >
                    <FontAwesomeIcon icon={currentIndex === idx ? faVolumeHigh : faMusic} className="mr-2 text-xs" />
                    <span className="mp-track-name">{track.name || "YouTube Track"}</span>
                </div>
            ))}
        </motion.div>
    );
};

// --- Main Component ---

export default function MusicPlayer() {
    const [tracks] = useState(() => shuffleArray(musicTracks));
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [trackTitle, setTrackTitle] = useState(() => tracks[0]?.name || "SoulLab_ Player");
    const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false);

    // Sync trackTitle with local tracks state immediately when index or track details change
    useEffect(() => {
        if (tracks[currentTrackIndex]) {
            setTrackTitle(tracks[currentTrackIndex].name || "SoulLab_ Player");
        }
    }, [currentTrackIndex, tracks]);

    const [volume, setVolume] = useState(() => parseFloat(localStorage.getItem('music_volume') || "0.3"));
    const [minimized, setMinimized] = useState(() => window.innerWidth < 768);
    const [showPlaylist, setShowPlaylist] = useState(false);
    const [loopMode, setLoopMode] = useState('all'); // 'none', 'one', 'all'
    const [isShuffle, setIsShuffle] = useState(false);

    const playerRef = useRef(null);
    const timerRef = useRef(null);
    const isPlayerInitializedRef = useRef(false);
    const errorCountRef = useRef(0);

    // UseRef to maintain stable access to state without triggering re-renders of the YouTube player
    const stateRef = useRef({
        volume,
        isPlaying,
        loopMode,
        currentTrackIndex,
        isShuffle,
        tracks
    });

    // Update the ref whenever these states change
    useEffect(() => {
        stateRef.current = { volume, isPlaying, loopMode, currentTrackIndex, isShuffle, tracks };
    }, [volume, isPlaying, loopMode, currentTrackIndex, isShuffle, tracks]);

    // Lazy load player on idle (after 3 seconds) to avoid freezing initial page load
    useEffect(() => {
        const timer = setTimeout(() => {
            setShouldLoadPlayer(true);
        }, 3000);
        return () => clearTimeout(timer);
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    const startTimer = useCallback(() => {
        stopTimer();
        timerRef.current = setInterval(() => {
            if (playerRef.current && playerRef.current.getCurrentTime) {
                setCurrentTime(playerRef.current.getCurrentTime());
            }
        }, 500);
    }, [stopTimer]);

    const handleNext = useCallback(() => {
        setShouldLoadPlayer(true);
        const { currentTrackIndex, isShuffle, tracks } = stateRef.current;
        let nextIndex = currentTrackIndex + 1;
        if (isShuffle) nextIndex = Math.floor(Math.random() * tracks.length);
        if (nextIndex >= tracks.length) nextIndex = 0;

        setCurrentTrackIndex(nextIndex);
        if (playerRef.current && playerRef.current.loadVideoById) {
            playerRef.current.loadVideoById(getYoutubeId(tracks[nextIndex].src));
        }
    }, []);

    const handlePrev = useCallback(() => {
        setShouldLoadPlayer(true);
        const { currentTrackIndex, tracks } = stateRef.current;
        let prevIndex = currentTrackIndex - 1;
        if (prevIndex < 0) prevIndex = tracks.length - 1;

        setCurrentTrackIndex(prevIndex);
        if (playerRef.current && playerRef.current.loadVideoById) {
            playerRef.current.loadVideoById(getYoutubeId(tracks[prevIndex].src));
        }
    }, []);

    const handleTrackEnd = useCallback(() => {
        const { loopMode } = stateRef.current;
        if (loopMode === 'one') {
            if (playerRef.current) {
                playerRef.current.seekTo(0);
                playerRef.current.playVideo();
            }
        } else {
            handleNext();
        }
    }, [handleNext]);

    const onPlayerReady = useCallback((event) => {
        const { volume, isPlaying } = stateRef.current;
        if (event.target.setVolume) event.target.setVolume(volume * 100);
        if (event.target.getDuration) setDuration(event.target.getDuration());

        try {
            if (event.target.getVideoData && event.target.getVideoData()) {
                const fetchedTitle = event.target.getVideoData().title;
                if (fetchedTitle) setTrackTitle(fetchedTitle);
            }
        } catch (e) {
            console.error("Error getting video data:", e);
        }

        if (isPlaying && event.target.playVideo) event.target.playVideo();
    }, []);

    const onPlayerStateChange = useCallback((event) => {
        if (event.data === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            if (event.target.getDuration) setDuration(event.target.getDuration());

            try {
                if (event.target.getVideoData && event.target.getVideoData()) {
                    const fetchedTitle = event.target.getVideoData().title;
                    if (fetchedTitle) setTrackTitle(fetchedTitle);
                }
            } catch (e) {
                console.error("Error getting video data on state change:", e);
            }
            startTimer();
        } else if (event.data === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
            stopTimer();
        } else if (event.data === window.YT.PlayerState.ENDED) {
            handleTrackEnd();
        }
    }, [startTimer, stopTimer, handleTrackEnd]);

    const initPlayer = useCallback((videoId) => {
        if (!videoId || isPlayerInitializedRef.current) return;
        isPlayerInitializedRef.current = true;
        if (playerRef.current && playerRef.current.destroy) {
            try {
                playerRef.current.destroy();
            } catch (e) {
                console.error("Error destroying player:", e);
            }
        }

        playerRef.current = new window.YT.Player('youtube-player-element', {
            height: '0',
            width: '0',
            videoId: videoId,
            playerVars: {
                autoplay: 0,
                controls: 0,
                modestbranding: 1,
                rel: 0,
            },
            events: {
                onReady: onPlayerReady,
                onStateChange: onPlayerStateChange,
                onError: (e) => {
                    console.error("Youtube Error:", e);
                    const { tracks } = stateRef.current;
                    if (errorCountRef.current < tracks.length) {
                        errorCountRef.current += 1;
                        console.log(`Skipping to next track due to error. Retry ${errorCountRef.current}/${tracks.length}`);
                        handleNext();
                    } else {
                        console.error("All tracks failed to load.");
                        setTrackTitle("Lỗi: Không phát được nhạc");
                        setIsPlaying(false);
                    }
                }
            }
        });
    }, [onPlayerReady, onPlayerStateChange, handleNext]);

    // Initialize YouTube API and Player when shouldLoadPlayer is true
    useEffect(() => {
        if (!shouldLoadPlayer) return;
        let isMounted = true;
        let checkInterval = null;

        const loadPlayer = () => {
            if (!isMounted) return;
            const { tracks: currentTracks } = stateRef.current;
            const initialVideoId = getYoutubeId(currentTracks[0]?.src);
            initPlayer(initialVideoId);
        };

        if (window.YT && window.YT.Player) {
            loadPlayer();
        } else {
            const previousCallback = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (previousCallback) previousCallback();
                loadPlayer();
            };

            if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
                const tag = document.createElement('script');
                tag.src = "https://www.youtube.com/iframe_api";
                const firstScriptTag = document.getElementsByTagName('script')[0];
                if (firstScriptTag && firstScriptTag.parentNode) {
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                } else {
                    document.head.appendChild(tag);
                }
            }

            checkInterval = setInterval(() => {
                if (window.YT && window.YT.Player) {
                    clearInterval(checkInterval);
                    loadPlayer();
                }
            }, 100);
        }

        return () => {
            isMounted = false;
            if (checkInterval) clearInterval(checkInterval);
            stopTimer();
            if (playerRef.current && playerRef.current.destroy) {
                try {
                    playerRef.current.destroy();
                } catch (e) {
                    // Ignore destroy errors on cleanup
                }
            }
        };
    }, [shouldLoadPlayer, initPlayer, stopTimer]);

    const togglePlay = useCallback(() => {
        if (!shouldLoadPlayer) {
            setShouldLoadPlayer(true);
            setIsPlaying(true);
            return;
        }
        if (!playerRef.current) return;
        const { isPlaying } = stateRef.current;
        if (isPlaying) {
            if (playerRef.current.pauseVideo) playerRef.current.pauseVideo();
        } else {
            if (playerRef.current.playVideo) playerRef.current.playVideo();
        }
    }, [shouldLoadPlayer]);

    const handleVolumeChange = useCallback((newVol) => {
        setVolume(newVol);
        if (playerRef.current && playerRef.current.setVolume) {
            playerRef.current.setVolume(newVol * 100);
        }
        localStorage.setItem('music_volume', newVol);
    }, []);

    const handleSeek = useCallback((e) => {
        const time = parseFloat(e.target.value);
        setCurrentTime(time);
        if (playerRef.current && playerRef.current.seekTo) {
            playerRef.current.seekTo(time);
        }
    }, []);

    return (
        <div id="music-player" className={minimized ? 'minimized' : ''} onClick={minimized ? () => setMinimized(false) : undefined}>
            <div id="youtube-player-element" style={{ display: 'none' }}></div>

            {minimized && (
                <div className={`mp-mini-icon ${!isPlaying ? 'paused' : ''}`}>
                    <FontAwesomeIcon icon={faCompactDisc} />
                </div>
            )}

            <div style={{ display: minimized ? 'none' : 'contents' }}>
                <div className="mp-main">
                    <div className="mp-info">
                        <TrackInfo
                            title={trackTitle}
                            currentTime={currentTime}
                            duration={duration}
                            isPlaying={isPlaying}
                        />
                    </div>
                    <div className="mp-actions">
                        <button className="mp-btn" onClick={() => setShowPlaylist(!showPlaylist)} title="Danh sách phát">
                            <FontAwesomeIcon icon={faListUl} />
                        </button>
                        <button className="mp-btn text-xs" onClick={(e) => { e.stopPropagation(); setMinimized(true); }} title="Thu nhỏ">
                            <FontAwesomeIcon icon={faCompress} />
                        </button>
                    </div>
                </div>

                <ProgressBar currentTime={currentTime} duration={duration} onSeek={handleSeek} />

                <Controls
                    isPlaying={isPlaying}
                    isShuffle={isShuffle}
                    loopMode={loopMode}
                    volume={volume}
                    onTogglePlay={togglePlay}
                    onNext={() => {
                        errorCountRef.current = 0;
                        handleNext();
                    }}
                    onPrev={() => {
                        errorCountRef.current = 0;
                        handlePrev();
                    }}
                    onShuffle={() => setIsShuffle(!isShuffle)}
                    onLoop={() => setLoopMode(loopMode === 'all' ? 'one' : (loopMode === 'one' ? 'none' : 'all'))}
                    onVolumeChange={handleVolumeChange}
                />

                <AnimatePresence>
                    {showPlaylist && (
                        <Playlist
                            tracks={tracks}
                            currentIndex={currentTrackIndex}
                            onSelect={(idx) => {
                                setShouldLoadPlayer(true);
                                errorCountRef.current = 0;
                                setCurrentTrackIndex(idx);
                                if (playerRef.current && playerRef.current.loadVideoById) {
                                    playerRef.current.loadVideoById(getYoutubeId(tracks[idx].src));
                                }
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
